import qs from "qs";
import { store } from "../store";

// Raw binary fetch for endpoints that return a file (PDF / CSV) rather than JSON.
// The shared HttpRequest pipes everything through HttpHandler which parses JSON,
// so it cannot be reused for binary bodies. This helper mirrors HttpRequest's
// auth (Bearer token from the redux auth slice + credentials) and uses a relative
// URL so the CRA dev proxy (and prod same-origin nginx) routes it correctly.

type Params = Record<string, string | number | boolean | undefined | null>;

function authHeaders(): HeadersInit {
  const headers: Record<string, string> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state = store.getState() as any;
  const accessToken = state?.auth?.accessToken;
  if (typeof accessToken === "string" && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const deviceToken = state?.auth?.deviceToken;
  if (typeof deviceToken === "string" && deviceToken) {
    headers["X-Device"] = deviceToken;
  }
  return headers;
}

async function fetchBlob(api: string, params?: Params): Promise<Blob> {
  const cleaned = params
    ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
    : undefined;
  const queryString = cleaned && Object.keys(cleaned).length ? `?${qs.stringify(cleaned)}` : "";
  const res = await fetch(`${api}${queryString}`, {
    method: "GET",
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Request failed with status ${res.status}`);
  }
  return res.blob();
}

/** Fetch a file endpoint and open it in a new browser tab (used for inline PDFs). */
export async function openFileInNewTab(api: string, params?: Params): Promise<void> {
  const blob = await fetchBlob(api, params);
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  // Revoke after a delay so the new tab has time to load the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) {
    // Popup blocked: fall back to a download so the user still gets the file.
    triggerDownload(blob, "download");
  }
}

/** Fetch a file endpoint and trigger a browser download with the given filename. */
export async function downloadFile(api: string, filename: string, params?: Params): Promise<void> {
  const blob = await fetchBlob(api, params);
  triggerDownload(blob, filename);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
